/**
 * C2 — whose data is at stake, and now also the choice of whose.
 *
 * It occupies the slot `<ActingAs>` already held: between the headline and the
 * adoption line, inside C1's block, as a LINE of the paragraph the headline
 * opens. Not a section, not a row with its own seams. The app is what the page
 * is about and keeps the prominence; this qualifies the sentence just read.
 *
 * IT IS A LINK, NOT A <select>. Chrome hands a native select's open state to the
 * operating system, which on macOS paints a dark rounded sheet that no rule on
 * this page can reach — the one control here that would not look like this page.
 * With one alternative there is nothing to open at all; past that it opens a
 * plate carrying the realm chooser's own filter and list, which is the page's
 * existing answer to "choose one of many" and caps itself at five rows.
 *
 * SWITCHING RE-PREVIEWS. The caller sets `group_id` and the page refetches,
 * because a group resolves a different ceiling, different realms and a different
 * schema closure. Relabelling the line while the ask below it still described
 * your own graph would be the exact failure this line exists to prevent.
 */
import React, { useEffect, useRef, useState } from 'react';
import { alternatives, hasChoice, selectedActor } from './actors';

/** Enough rows that a filter earns its place — the realms use the same number. */
const FILTER_AT = 6;

function Plate({ actors, groupId, onPick, onClose }) {
  const box = useRef(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away, true);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? actors.filter((a) => a.name.toLowerCase().includes(needle))
    : actors;
  const on = selectedActor(actors, groupId);

  return (
    <div className="gp-actor__plate" ref={box} data-testid="actor-plate">
      {actors.length >= FILTER_AT && (
        <div className="gp-filter">
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Filter" aria-label="Filter who this grant is for" />
          <span>{shown.length}</span>
        </div>
      )}
      {/* A GROUP OF BUTTONS, NOT A LISTBOX. It announced `role="listbox"` while
          offering none of what that promises — no arrow keys, no
          aria-activedescendant — so a screen-reader user was told to expect a
          widget that was not there. These are buttons, Tab reaches them, and
          `aria-pressed` says which one is in force. Honest, and it is what the
          realm rows above already are. */}
      <div className={`gp-actor__list${shown.length > 5 ? ' is-capped' : ''}`}
           role="group" aria-label="Whose data this grant is for">
        {shown.map((a) => (
          <button key={a.id || 'you'} type="button" aria-pressed={a === on}
                  className={`gp-actor__row${a === on ? ' is-sel' : ''}`}
                  onClick={() => { onPick(a.id); onClose(); }}>
            <span className="gp-actor__box" aria-hidden="true" />
            <span className="gp-actor__body">
              <span className="gp-actor__name">{a.name}</span>
              <span className="gp-actor__meta">
                {a.kind === 'you' ? 'you · your own graph' : `${a.role} · group`}
              </span>
            </span>
          </button>
        ))}
        {shown.length === 0 && <p className="gp-actor__none">No group by that name.</p>}
      </div>
    </div>
  );
}

/**
 * ONE OWNER OF THE SENTENCE. This component draws C2 only when there IS a
 * choice; with none the route renders `<ActingAs>`, which is the version that
 * has always shipped. An internal fallback stood here briefly and was deleted:
 * the route already gates on `hasChoice`, so it was unreachable, and being
 * unreachable it had quietly drifted from the real one (it dropped the
 * "of <b>name</b>" clause). Two copies of a load-bearing sentence, one of them
 * never rendered, is how the rendered one stops being reviewed.
 *
 * @param {object}   props
 * @param {Array}    props.actors   from `actorsFor` — you, then groups you admin.
 * @param {string}   [props.groupId] the id in force, or falsy for you.
 * @param {Function} props.onPick   called with a group id, or null for you.
 * @param {string}   [props.error]  a switch that failed, said here rather than
 *   as the page: the consent screen underneath is still valid and still theirs.
 */
export default function ActingChoice({ actors = [], groupId, onPick, error }) {
  const [open, setOpen] = useState(false);

  if (!hasChoice(actors)) return null;

  const on = selectedActor(actors, groupId);
  const others = alternatives(actors, groupId);
  const one = others.length === 1 ? others[0] : null;

  return (
    <p className="gp-acting gp-acting--pick" data-testid="c2">
      <span className="gp-acting__k">Whose data</span>
      <span className="gp-acting__v">
        <b>{on.name}</b>
        {on.kind === 'you'
          ? ' — your own graph'
          : <> — as <b>{on.role}</b>, your own isn&rsquo;t involved</>}
      </span>
      <span className="gp-acting__alt">
        Grant for{' '}
        <span className="gp-actor">
          <button type="button" className={one ? 'gp-link' : 'gp-actor__t'}
                  aria-haspopup={one ? undefined : 'listbox'}
                  aria-expanded={one ? undefined : open}
                  onClick={() => (one ? onPick(one.id) : setOpen((v) => !v))}>
            {one ? one.name : `one of your ${others.length} groups`}
          </button>
          {open && !one && (
            <Plate actors={actors} groupId={groupId}
                   onPick={onPick} onClose={() => setOpen(false)} />
          )}
        </span>
        {' '}instead.
      </span>
      {error && (
        <span className="gp-acting__err" role="alert" data-testid="c2-error">{error}</span>
      )}
    </p>
  );
}
